import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { useAuth } from '@/contexts/auth';
import { Navigate } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Grid2X2, Package, Users, ShoppingBag, Settings, User2, BarChart, ShieldAlert, Store } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import DashboardOverview from '@/components/admin/DashboardOverview';
import RecentActivities from '@/components/admin/RecentActivities';
import SystemHealth from '@/components/admin/SystemHealth';
import { Skeleton } from '@/components/ui/skeleton';
import AhavexerManagement from '@/components/admin/AhavexerManagement';
import UsersManagement from '@/components/admin/UsersManagement';
import OrdersManagement from '@/components/admin/OrdersManagement';
import ProductsManagement from '@/components/admin/ProductsManagement';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from '@/components/ui/table';

const AdminDashboard: React.FC = () => {
  const { currentUser, userType } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const { data: dashboardStats, isLoading, error } = useDashboardStats();

  // Redirect if not logged in or not an admin
  if (!currentUser || userType !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return (
    <Layout hideSidebar>
      <div className="container mx-auto py-6 px-4 md:px-6 max-w-[1400px]">
        <div className="grid grid-cols-1 gap-6">
          {/* Admin Dashboard Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-6 shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
              {/* Left Column - Admin Portal */}
              <div className="md:col-span-1">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-6 w-6 text-white" />
                    <h1 className="text-2xl md:text-3xl font-bold text-white">Admin Portal</h1>
                  </div>
                  <p className="text-blue-100">Manage the entire Ahavex platform</p>
                  
                  <div className="flex items-center gap-4 pt-3">
                    <Avatar className="h-16 w-16 border-2 border-white bg-white">
                      <AvatarFallback className="bg-blue-100 text-blue-600 text-xl">
                        {currentUser.email?.charAt(0).toUpperCase() || 'A'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-lg text-white break-all">{currentUser.email}</p>
                      <p className="text-blue-100">Administrator</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right Column - Platform Stats */}
              <div className="md:col-span-2">
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                    <BarChart className="h-5 w-5" />
                    Platform Overview
                  </h2>
                  <p className="text-blue-100">Global metrics and statistics</p>
                  
                  <DashboardOverview 
                    totalUsers={dashboardStats?.totalUsers || 0}
                    totalAhavexers={dashboardStats?.totalAhavexers || 0}
                    totalProducts={dashboardStats?.totalProducts || 0}
                    totalRevenue={dashboardStats?.totalRevenue || 0}
                    isLoading={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Tabs Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="overflow-x-auto">
                <TabsList className="w-full h-auto p-0 bg-transparent border-b border-gray-100 rounded-none">
                  <TabsTrigger 
                    value="overview" 
                    className="py-3 px-6 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none"
                  >
                    Overview
                  </TabsTrigger>
                  <TabsTrigger 
                    value="users" 
                    className="py-3 px-6 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none"
                  >
                    Users
                  </TabsTrigger>
                  <TabsTrigger 
                    value="ahavexers" 
                    className="py-3 px-6 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none"
                  >
                    Ahavexers
                  </TabsTrigger>
                  <TabsTrigger 
                    value="products" 
                    className="py-3 px-6 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none"
                  >
                    Products
                  </TabsTrigger>
                  <TabsTrigger 
                    value="orders" 
                    className="py-3 px-6 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none"
                  >
                    Orders
                  </TabsTrigger>
                  <TabsTrigger 
                    value="settings" 
                    className="py-3 px-6 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none"
                  >
                    Settings
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="overview" className="p-6">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-gray-700">Platform Overview</h3>
                    <Button variant="outline" size="sm">
                      <BarChart className="h-4 w-4 mr-2" />
                      Generate Report
                    </Button>
                  </div>
                  
                  {error && (
                    <div className="bg-red-50 text-red-800 p-4 rounded-md mb-4">
                      Failed to load dashboard data. Please try again later.
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <RecentActivities 
                      activities={dashboardStats?.recentActivities || []}
                      isLoading={isLoading}
                    />
                    
                    <SystemHealth 
                      systemHealth={dashboardStats?.systemHealth || {
                        serverUptime: 0,
                        databasePerformance: 0,
                        apiResponseTime: 0,
                        storageCapacity: 0
                      }}
                      isLoading={isLoading}
                    />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="users" className="p-6">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-gray-700">User Management</h3>
                    <Button variant="outline" size="sm">
                      <Users className="h-4 w-4 mr-2" />
                      Export Users
                    </Button>
                  </div>
                  
                  <Card>
                    <CardContent className="p-0">
                      <UsersManagement />
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="ahavexers" className="p-6">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-gray-700">Ahavexer Management</h3>
                    <Button variant="outline" size="sm">
                      <Store className="h-4 w-4 mr-2" />
                      Export Ahavexers
                    </Button>
                  </div>
                  
                  <Card>
                    <CardContent className="p-0">
                      <AhavexerManagement />
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="products" className="p-6">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-gray-700">Product Management</h3>
                    <Button variant="outline" size="sm">
                      <Package className="h-4 w-4 mr-2" />
                      Export Products
                    </Button>
                  </div>
                  
                  <Card>
                    <CardContent className="p-0">
                      <ProductsManagement />
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="orders" className="p-6">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-gray-700">Order Management</h3>
                    <Button variant="outline" size="sm">
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      Export Orders
                    </Button>
                  </div>
                  
                  <Card>
                    <CardContent className="p-0">
                      <OrdersManagement />
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="settings" className="p-6">
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-700">System Settings</h3>
                  <p className="text-gray-500">Manage global platform settings</p>
                  
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-medium mb-4">Admin Account Information</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500">Email</p>
                        <p className="text-sm font-semibold">{currentUser.email}</p>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500">Account Type</p>
                        <p className="text-sm font-semibold">Administrator</p>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
